import React, { createContext, useState, useEffect, useContext, useRef } from 'react';

import api from './api'
import { getAccessToken, setAccessToken, removeAccessToken } from '../utils/Storage'

interface User {
  id: number;
  name: string;
  username: string;
  email: string;
}

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const Auth = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(Auth);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      return;
    }

    // Check if user is logged in on page load
    const token = getAccessToken();
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Try to load user data using the token
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/v1/profile/');
      setCurrentUser(response.data.user);
      setIsAuthenticated(true);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching current user:', err);
      // Clear invalid token
      removeAccessToken();
      delete api.defaults.headers.common['Authorization'];
      setCurrentUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/v1/login/', { username, password });

      setAccessToken(response.data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

      setCurrentUser(response.data.user);
      setIsAuthenticated(true);
    } catch (err) {
      const error = err as { response?: { data?: { errors?: any; message?: string } } };
      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to login. Please check your credentials and try again.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (username: string, email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/v1/register/', {
        username,
        email,
        password,
        password2: password
      });

      setAccessToken(response.data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

      setCurrentUser(response.data.user);
      setIsAuthenticated(true);
    } catch (err) {
      if (err && typeof err === 'object' && 'response' in err) {
        const error = err as { response?: { data?: { errors?: any; message?: string } } };
        if (error.response && error.response?.data && error.response?.data.errors) {
          const errorMessage = Object.values(error.response.data.errors).flat()[0] as string;
          setError(errorMessage);
        } else if (error.response && error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else {
          setError('Registration failed. Please try again.');
        }
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    removeAccessToken();
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated,
  };

  return <Auth.Provider value={value}>{children}</Auth.Provider>;
};